import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { DataTableFilterField } from '@r/types/index.ts';
import { isTargetLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import { UserFullname } from '@r/components/user-fullname.tsx';

export interface PerformanceCollection {
  id?: number;
  amount?: number | null;
  label?: string | null;
  realizedAt?: string | null;
  createdAt?: string;
  realizedBy?: { fullname?: string | null; isDeleted?: boolean } | null;
  realizedByCompany?: { name?: string | null; uniqueCode?: string | null } | null;
  logicTypeTarget?: { reference?: string | null } | null;
  addedByUserWorkspace?: { user?: { fullname?: string | null; isDeleted?: boolean } } | null;
  referenceUniqueCode?: string | null;
  referenceQuantity?: number | null;
  beneficiaryFullname?: string;
  point?: { amount?: number | null } | null;
}

export const usePerformancesFilterFields = (): DataTableFilterField<PerformanceCollection>[] => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);

  return [
    {
      label: t('label.performance.company.name'),
      value: 'companyName',
      placeholder: t('label.performance.company.name')
    },
    {
      label: t('label.performance.unique.code'),
      value: 'companyUniqueCode',
      placeholder: t('label.performance.unique.code')
    },
    {
      label: t('label.performances.beneficiary'),
      value: 'beneficiaryFullname',
      placeholder: t('label.performances.beneficiary')
    },
    {
      label: t('label.performances.amount'),
      value: 'amount',
      placeholder: t('label.performances.amount')
    },
    {
      label: t('label.performance.pattern'),
      value: 'label',
      placeholder: t('label.performance.pattern')
    },
    ...(isTargetLogicWorkspace(workspace)
      ? [
          {
            label: t('label.performance.target.reference'),
            value: 'logicTypeTargetReference',
            placeholder: t('label.performance.target.reference')
          }
        ]
      : []),
    {
      label: t('label.performances.reference.unique.code'),
      value: 'referenceUniqueCode',
      placeholder: t('label.performances.reference.unique.code')
    },
    {
      label: t('label.performances.reference.quantity'),
      value: 'referenceQuantity',
      placeholder: t('label.performances.reference.quantity')
    },
    {
      label: t('label.performance.realized.date'),
      value: 'realizedAt',
      placeholder: t('label.performance.realized.date'),
      isDateRange: true
    },
    {
      label: t('label.performance.import.date'),
      value: 'createdAt',
      placeholder: t('label.performance.import.date'),
      isDateRange: true
    },
    {
      label: t('label.point.import.user.fullname'),
      value: 'importedByFullname',
      placeholder: t('label.point.import.user.fullname')
    }
  ];
};

export const usePerformancesColumns = (): ColumnDef<PerformanceCollection>[] => {
  const { t } = useTranslation();
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const formatTargetPoints = (count?: number | null) => {
    const safeCount = count ?? 0;
    return FormattedNumber({ value: safeCount, style: 'decimal' });
  };

  return [
    {
      id: 'companyName',
      accessorKey: 'company.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.name')} />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => row.original.realizedByCompany?.name ?? ''
    },
    {
      id: 'companyUniqueCode',
      accessorKey: 'company.uniqueCode',
      header: t('label.company.unique.code'),
      cell: ({ row }) => row.original.realizedByCompany?.uniqueCode ?? ''
    },
    {
      id: 'beneficiaryFullname',
      accessorKey: 'user',
      header: t('label.performances.beneficiary'),
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.realizedBy?.fullname ?? ''}
          isDeleted={row.original.realizedBy?.isDeleted}
        />
      )
    },
    {
      accessorKey: 'amount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.performances.amount')} />
      ),
      enableHiding: false,
      cell: ({ row }) => <FormattedNumber value={row.original.amount ?? 0} />
    },
    {
      id: 'label',
      accessorKey: 'label',
      header: t('label.point.pattern'),
      cell: ({ row }) => row.original.label ?? ''
    },
    ...(isTargetLogicWorkspace(workspace)
      ? [
          {
            accessorKey: 'logicTypeTarget',
            header: t('label.performances.target.reference'),
            cell: ({ row }: CellContext<PerformanceCollection, unknown>) => {
              return row.original.logicTypeTarget?.reference ?? '';
            }
          }
        ]
      : []),
    {
      accessorKey: 'referenceUniqueCode',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('label.performances.reference.unique.code')}
        />
      ),
      cell: ({ row }) => row.original.referenceUniqueCode ?? ''
    },
    {
      accessorKey: 'referenceQuantity',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.performances.reference.quantity')} />
      ),
      cell: ({ row }) => row.original.referenceQuantity ?? ''
    },
    {
      id: 'point.amount',
      accessorFn: (row) => row.point?.amount ?? 0,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.point.target.amount')} />
      ),
      cell: ({ row }: CellContext<PerformanceCollection, unknown>) =>
        formatTargetPoints(row.original.point?.amount)
    },
    {
      id: 'realizedAt',
      accessorKey: 'realizedAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.performances.realization.date')} />
      ),
      enableHiding: false,
      cell: ({ row }) =>
        row.original.realizedAt
          ? format(new Date(row.original.realizedAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.performances.import.date')} />
      ),
      enableHiding: false,
      cell: ({ row }) =>
        row.original.createdAt
          ? format(new Date(row.original.createdAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    },
    {
      id: 'importedByFullname',
      accessorFn: (row) => row.addedByUserWorkspace?.user?.fullname ?? '',
      header: t('label.performances.import.user.fullname'),
      cell: ({ row }) => (
        <UserFullname
          fullname={row.original.addedByUserWorkspace?.user?.fullname ?? ''}
          isDeleted={row.original.addedByUserWorkspace?.user?.isDeleted}
        />
      )
    }
  ];
};
